import * as React from "react"
import { cn } from "@verlo/shared/lib/utils"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"

/**
 * Which site a page is about (Back office sweep, Oct 2026: D7). One rule everywhere:
 *
 * - A business with ONE site has nothing to pick. The field shows the site's name, with no
 *   chevron, and can't be opened.
 * - A business with SEVERAL sites gets a dropdown that reads "Please select", muted, until one
 *   is picked. `allowAll` adds "All sites" for a page that can show them together.
 *
 *   <SitePicker sites={sites} value={siteId} onChange={setSiteId} allowAll testId="pnl-site-select" />
 *
 * `value` "all" means every site; "" (or null) means nothing picked yet. A page where "" is
 * every site (`useFollowedSite('')`) passes `allValue=""`, so "" reads "All sites", not
 * "Please select". Radix can't take "" as an item value; it rides as a sentinel inside.
 */
const ALL = "__all_sites__"

function SitePicker({ sites, value, onChange, allowAll = false, allValue = "all", placeholder = "Please select",
  className, triggerClassName, testId, disabled = false }) {
  const list = sites || []
  const only = list.length === 1 ? list[0].id : null
  // One site and a form that needs a site chosen: it's that one. ("all" is left alone: with
  // one site it's the same thing.)
  const meansAll = allowAll && (value ?? "") === allValue
  React.useEffect(() => {
    if (only && value !== only && value !== "all" && !meansAll && onChange) onChange(only)
  }, [only, value]) // eslint-disable-line react-hooks/exhaustive-deps
  // No sites yet (still loading, or the list didn't come): an empty dropdown, not a name.
  if (list.length === 0) {
    return (
      <Select disabled>
        <SelectTrigger className={cn(triggerClassName, className)} data-testid={testId} aria-label="Site">
          <SelectValue placeholder={<span className="text-ink-faint">{placeholder}</span>} />
        </SelectTrigger>
      </Select>
    )
  }
  if (list.length === 1) {
    return (
      <div
        className={cn(
          "flex h-9 w-full items-center rounded-md border border-input bg-surface-2 px-3 text-sm text-ink",
          triggerClassName, className,
        )}
        data-testid={testId}
        data-single-site="true"
        aria-label="Site"
      >
        <span className="truncate">{list[0].name}</span>
      </div>
    )
  }
  return (
    <Select
      value={meansAll ? ALL : (value || undefined)}
      onValueChange={(v) => onChange(v === ALL ? allValue : v)}
      disabled={disabled}
    >
      <SelectTrigger className={cn(triggerClassName, className)} data-testid={testId} aria-label="Site">
        <SelectValue placeholder={<span className="text-ink-faint">{placeholder}</span>} />
      </SelectTrigger>
      <SelectContent>
        {allowAll && <SelectItem value={ALL}>All sites</SelectItem>}
        {list.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  )
}

export { SitePicker }
